<script setup lang="ts">
/* Sign in. There is deliberately no sign-up link and no password
   reset: admin accounts are created with the service role and
   granted a `platform_admins` row out of band, so there is nothing
   here a stranger could usefully start. */
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TextField from '@/components/ui/TextField.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import { HOME_ROUTE } from '@/config/routes'
import { useAuth, SignInError } from '@/lib/auth'

const router = useRouter()
const route = useRoute()
const { signIn } = useAuth()

/**
 * Where to go after signing in. The guard puts the blocked path in `?next`,
 * but a query string is attacker-controllable, so only a plain in-app path
 * is honoured: anything protocol-relative (`//evil.com`) or absolute would
 * turn this form into an open redirect.
 */
function destination(): string {
  const next = route.query.next
  const path = typeof next === 'string' ? next : ''
  return path.startsWith('/') && !path.startsWith('//') ? path : HOME_ROUTE
}

const email = ref('')
const password = ref('')
const error = ref('')
const busy = ref(false)

async function onSubmit(): Promise<void> {
  if (busy.value) return
  busy.value = true
  error.value = ''

  try {
    await signIn(email.value, password.value)
    await router.push(destination())
  } catch (e) {
    error.value =
      e instanceof SignInError ? e.message : 'Could not sign in. Check your connection and try again.'
    password.value = ''
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <form class="form" novalidate @submit.prevent="onSubmit">
    <div class="headings">
      <h1 class="title">Sign in</h1>
      <p class="subtitle">Admin access to River Atlas.</p>
    </div>

    <Alert v-if="error" tone="danger">{{ error }}</Alert>

    <TextField
      v-model="email"
      label="Email"
      type="email"
      name="email"
      autocomplete="username"
      placeholder="you@riveratlas.eu"
      required
    />

    <TextField
      v-model="password"
      label="Password"
      type="password"
      name="password"
      autocomplete="current-password"
      required
    />

    <Button type="submit" full-width :loading="busy">Sign in</Button>
  </form>
</template>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.headings {
  margin-bottom: var(--space-3xs);
}

.title {
  font-size: var(--text-lg);
  letter-spacing: var(--tracking-tight);
}

.subtitle {
  margin-top: var(--space-3xs);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>
